import axios, {AxiosError, AxiosInstance, AxiosRequestConfig} from "axios";
import {getSessionData} from "../../mixpanel";
import {logoutReset} from "../../react-query-hooks/logout-dispatch";
import {getToken} from "./tokenManager";

export interface HttpClientConfig extends AxiosRequestConfig {
  token?: string;
}

export interface HttpClientError {
  status: boolean;
  message: string;
}

/**
 * Creates a configured axios instance with session data injection,
 * JWT token handling, and 401 logout behavior.
 */
const createAxiosInstance = (): AxiosInstance => {
  const instance = axios.create();

  // Add session data to headers
  instance.interceptors.request.use(
    (config) => {
      const session = typeof getSessionData === "function" ? getSessionData() : undefined;

      if (session && session.device_info) {
        const d = session.device_info;
        const g = session.geographic_location || {};

        config.headers = {
          ...config.headers,
          "X-Session-Id": session.session_id,
          "X-Device-Id": session.device_id,
          "X-UTM-Source": session.utm_source,
          "X-UTM-Campaign": session.utm_campaign,
          "X-UTM-Medium": session.utm_medium,
          "X-Page-Url": session.page_url,
          "X-Operating-System": d.os || "Unknown",
          "X-OS-Version": d.os_version || "Unknown",
          "X-Browser-Type": d.browser || "Unknown",
          "X-Browser-Version": d.browser_version || "Unknown",
          "X-Device-Type": d.device || "Unknown",
          "X-Device-Model": d.device_model || "Unknown",
          "X-Country": g.country || "Unknown",
          "X-Region": g.region || "Unknown",
          "X-City": g.city || "Unknown",
        };
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  return instance;
};

/**
 * Creates an HTTP client bound to a specific base URL.
 * Handles JWT token injection and 401 logout behavior.
 */
const createHttpClient = (baseUrl: string) => {
  const axiosInstance = createAxiosInstance();

  const request = async <T = unknown>(
    url: string,
    {token, headers: customHeaders, ...customConfig}: HttpClientConfig = {}
  ): Promise<T> => {
    // Use explicit token if provided, otherwise get from tokenManager
    const authToken = token ?? getToken();

    const config: AxiosRequestConfig = {
      baseURL: baseUrl,
      url,
      headers: {
        ...(authToken && {Authorization: `Bearer ${authToken}`}),
        ...customHeaders,
      },
      ...customConfig,
    };

    try {
      const response = await axiosInstance.request<T>(config);
      return response.data;
    } catch (err) {
      const axiosError = err as AxiosError<HttpClientError>;

      if (axiosError.response?.status === 401) {
        logoutReset();
        window.location.assign(`${window.location.origin}/auth/login`);
      }

      const responseError = axiosError.response?.data;
      return Promise.reject({
        status: responseError?.status ?? false,
        message:
          responseError?.message ||
          axiosError.message ||
          "The network request could not be completed",
      } satisfies HttpClientError);
    }
  };

  return {
    get: <T = unknown>(url: string, config?: HttpClientConfig) =>
      request<T>(url, {...config, method: "GET"}),

    post: <T = unknown>(url: string, data?: unknown, config?: HttpClientConfig) =>
      request<T>(url, {...config, method: "POST", data}),

    put: <T = unknown>(url: string, data?: unknown, config?: HttpClientConfig) =>
      request<T>(url, {...config, method: "PUT", data}),

    patch: <T = unknown>(url: string, data?: unknown, config?: HttpClientConfig) =>
      request<T>(url, {...config, method: "PATCH", data}),

    delete: <T = unknown>(url: string, config?: HttpClientConfig) =>
      request<T>(url, {...config, method: "DELETE"}),

    request,
  };
};

// API Base URLs from environment
const API_BASE_URL_V1 =
  process.env.REACT_APP_APP_ENV === "production"
    ? process.env.REACT_APP_API_BASE_URL_LIVE
    : process.env.REACT_APP_API_BASE_URL_DEV;

const API_BASE_URL_V2 =
  process.env.REACT_APP_APP_ENV === "production"
    ? process.env.REACT_APP_API_BASE_URL_LIVE_V2
    : process.env.REACT_APP_API_BASE_URL_DEV_V2;

// Pre-configured HTTP clients for v1 and v2 APIs.
// httpClientV1 is intentionally retained: v1 is decommissioned everywhere
// except 2 documented exceptions with no v2 endpoint yet (live-class draft
// save/list, and the Stripe dashboard login link). Do not add new v1 usages.
export const httpClientV1 = createHttpClient(API_BASE_URL_V1 || "");
export const httpClientV2 = createHttpClient(API_BASE_URL_V2 || "");

export {createHttpClient};
